import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { mkdir, readFile } from 'node:fs/promises';
import sharp from 'sharp';

// One libvips thread per pipeline on the main thread too — same reasoning as `split-worker.js`. Guarded because
// this module is reachable from the client bundle, where `sharp` is shimmed away.
try {
  sharp.concurrency(1);
} catch {
  /* not on the server */
}
import { LLM_IMAGE_JPEG_QUALITY, LLM_IMAGE_MAX_PX, LLM_PAYLOAD_SAFE_LIMIT_BYTES } from '../../shared/constants.js';
import { encodeImagesInWorker, type EncodeEntry } from './run-in-worker.js';

function fmtMem(): string {
  const m = process.memoryUsage();
  return `rss=${(m.rss / 1024 / 1024).toFixed(0)}MB heap=${(m.heapUsed / 1024 / 1024).toFixed(0)}MB ext=${(m.external / 1024 / 1024).toFixed(0)}MB`;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type FileToBase64Options = {
  ocrEnhance?: boolean;
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type ResizeForLLMOptions = {
  maxPx?: number;
  jpegQuality?: number;
  ocrEnhance?: boolean;
};

export type SizeFallbackOptions = ResizeForLLMOptions & {
  safeLimitBytes?: number;
  logTag?: string;
};

async function enhanceImageForOCR(fullPath: string): Promise<string> {
  const inputBuffer = await readFile(fullPath);
  const outputBuffer = await sharp(inputBuffer).grayscale().normalize().threshold(160).toFormat('png').toBuffer();
  return outputBuffer.toString('base64');
}

export async function fileToBase64URL(file: AdornisFile, options: FileToBase64Options = {}) {
  const isImageFile = file instanceof ImageFile || file._class === ImageFile._class;
  const fileEntity = isImageFile ? new ImageFile(file) : new AdornisFile(file);
  const fullPath = fileEntity.getFullPath();
  const mimetype = fileEntity.meta?.mimetype ?? 'image/png';
  if (options.ocrEnhance && isImageFile) {
    try {
      const enhancedBase64 = await enhanceImageForOCR(fullPath);
      return `data:image/png;base64,${enhancedBase64}`;
    } catch {
      // Fallback to original image if enhancement fails.
    }
  }
  return `data:${mimetype};base64,${await readFile(fullPath, { encoding: 'base64' })}`;
}

// Total size in bytes of the decoded base64 part of a list of data URLs (after the comma).
// This is what counts against OpenRouter's request-body limit.
export function calcTotalBase64Bytes(urls: string[]): number {
  return urls.reduce((sum, url) => sum + ((url.includes(',') ? url.split(',')[1] : url) ?? '').length, 0);
}

// Resize a single image from disk and return a base64 data URL.
// JPEG by default; when ocrEnhance is true, chains grayscale + normalize + threshold(160) and outputs PNG
// so the OCR pre-processing is preserved on the fallback path.
export async function resizeImageToBase64URL(
  file: ImageFile | AdornisFile,
  options: ResizeForLLMOptions = {},
): Promise<string> {
  const { maxPx = LLM_IMAGE_MAX_PX, jpegQuality = LLM_IMAGE_JPEG_QUALITY, ocrEnhance = false } = options;
  const isImageFile = file instanceof ImageFile || file._class === ImageFile._class;
  const fileEntity = isImageFile ? new ImageFile(file) : new AdornisFile(file);
  const fullPath = fileEntity.getFullPath();

  const pipeline = sharp(fullPath).resize({ width: maxPx, height: maxPx, fit: 'inside', withoutEnlargement: true });
  if (ocrEnhance) {
    const buffer = await pipeline.grayscale().normalize().threshold(160).toFormat('png').toBuffer();
    return `data:image/png;base64,${buffer.toString('base64')}`;
  }
  const buffer = await pipeline.jpeg({ quality: jpegQuality }).toBuffer();
  return `data:image/jpeg;base64,${buffer.toString('base64')}`;
}

// Resize an image whose contents are already encoded as a base64 data URL — no DB or disk I/O.
// Use in fallback paths where the agent already has the bytes in memory (a previously cached or
// converted base64 URL): re-fetching the file entity from MongoDB and re-reading from disk just
// to resize the same bytes is wasteful and shows up as `getEntityByID` resolver-performance log
// noise. Sharp can decode a base64 buffer directly.
//
// Note: this resizes whatever is in the URL — if the URL contains a transformed image (e.g. the
// OCR-enhanced PNG produced by fileToBase64URL with `ocrEnhance: true`), this resizes the
// transformed image, not the original. Callers that need to resize the original from disk
// should use resizeImageToBase64URL instead.
export async function resizeBase64URLToBase64URL(dataUrl: string, options: ResizeForLLMOptions = {}): Promise<string> {
  const { maxPx = LLM_IMAGE_MAX_PX, jpegQuality = LLM_IMAGE_JPEG_QUALITY, ocrEnhance = false } = options;
  const base64 = dataUrl.includes(',') ? (dataUrl.split(',')[1] ?? '') : dataUrl;
  const inputBuffer = Buffer.from(base64, 'base64');

  const pipeline = sharp(inputBuffer).resize({
    width: maxPx,
    height: maxPx,
    fit: 'inside',
    withoutEnlargement: true,
  });
  if (ocrEnhance) {
    const buffer = await pipeline.grayscale().normalize().threshold(160).toFormat('png').toBuffer();
    return `data:image/png;base64,${buffer.toString('base64')}`;
  }
  const buffer = await pipeline.jpeg({ quality: jpegQuality }).toBuffer();
  return `data:image/jpeg;base64,${buffer.toString('base64')}`;
}

// High-level: convert all images via fileToBase64URL (passing through ocrEnhance), log the union size,
// and if it exceeds safeLimitBytes re-convert every image via resizeImageToBase64URL.
// Use for agents that have file entities and a single conversion point per LLM call.
// Agents that combine cached URLs from multiple per-image lookups should instead use the primitives
// (calcTotalBase64Bytes + resizeImageByIdToBase64URL) at the combine site so the cache is preserved.
//
// Both passes are dispatched to a worker_threads worker so the main event loop stays free to
// flush BaseQL keepalives during big-PDF runs (e.g. the split agent). The main thread only
// resolves entity → path/mimetype here.
function buildEncodeEntry(
  file: ImageFile | AdornisFile,
  ocrEnhance: boolean,
  resize: boolean,
  maxPx?: number,
  jpegQuality?: number,
): EncodeEntry {
  const isImageFile = file instanceof ImageFile || file._class === ImageFile._class;
  const fileEntity = isImageFile ? new ImageFile(file) : new AdornisFile(file);
  return {
    fullPath: fileEntity.getFullPath(),
    isImageFile,
    mimetype: fileEntity.meta?.mimetype ?? 'image/png',
    ocrEnhance,
    resize,
    maxPx,
    jpegQuality,
  };
}

export async function imagesToBase64URLsWithSizeFallback(
  images: (ImageFile | AdornisFile)[],
  options: SizeFallbackOptions = {},
): Promise<string[]> {
  if (!images.length) return [];
  const {
    safeLimitBytes = LLM_PAYLOAD_SAFE_LIMIT_BYTES,
    logTag = '[LLM]',
    ocrEnhance = false,
    maxPx,
    jpegQuality,
  } = options;

  // If every file's on-disk size is known and the raw base64 total already exceeds the limit,
  // skip pass 1 entirely — it would only produce a payload we immediately discard.
  // base64 inflates raw bytes by 4/3, so diskBytes * (4/3) gives a tight upper-bound estimate.
  const diskSizeBytes = images.reduce((sum, f) => sum + (((f as any).meta?.size as number) || 0), 0);
  const allSizesKnown = diskSizeBytes > 0 && images.every(f => ((f as any).meta?.size as number) > 0);
  const skipPass1 = allSizesKnown && diskSizeBytes * (4 / 3) > safeLimitBytes;
  const limitMB = (safeLimitBytes / 1024 / 1024).toFixed(0);

  let urls: string[];
  let totalBytes: number;

  if (skipPass1) {
    const effectiveMaxPx = maxPx ?? LLM_IMAGE_MAX_PX;
    const effectiveJpegQuality = jpegQuality ?? LLM_IMAGE_JPEG_QUALITY;
    logger.info(
      {
        context: 'encode:skip-pass1',
        diskMB: (diskSizeBytes / 1024 / 1024).toFixed(1),
        estimatedBase64MB: ((diskSizeBytes * (4 / 3)) / 1024 / 1024).toFixed(1),
        limitMB,
        mem: fmtMem(),
      },
      'Disk size exceeds limit — going straight to resize',
    );
    logger.info(
      { context: 'encode:start', pass: 2, images: images.length, resize: true, maxPx: effectiveMaxPx, mem: fmtMem() },
      'Starting encode pass',
    );
    urls = await encodeImagesInWorker(
      images.map(f => buildEncodeEntry(f, ocrEnhance, true, effectiveMaxPx, effectiveJpegQuality)),
    );
    totalBytes = calcTotalBase64Bytes(urls);
    logger.info(
      {
        context: 'encode:done',
        pass: 2,
        images: urls.length,
        payloadMB: (totalBytes / 1024 / 1024).toFixed(2),
        mem: fmtMem(),
      },
      'Encode pass complete',
    );
    logger.info(
      { context: logTag, payloadMB: (totalBytes / 1024 / 1024).toFixed(2), images: urls.length },
      'After resize payload ready',
    );
  } else {
    logger.info(
      { context: 'encode:start', pass: 1, images: images.length, ocrEnhance, mem: fmtMem() },
      'Starting encode pass',
    );
    urls = await encodeImagesInWorker(images.map(f => buildEncodeEntry(f, ocrEnhance, false)));
    totalBytes = calcTotalBase64Bytes(urls);
    logger.info(
      {
        context: 'encode:done',
        pass: 1,
        images: urls.length,
        payloadMB: (totalBytes / 1024 / 1024).toFixed(2),
        limitMB,
        mem: fmtMem(),
      },
      'Encode pass complete',
    );
    logger.info(
      { context: logTag, payloadMB: (totalBytes / 1024 / 1024).toFixed(2), images: urls.length, limitMB },
      'Payload size after pass 1',
    );

    if (totalBytes > safeLimitBytes) {
      const effectiveMaxPx = maxPx ?? LLM_IMAGE_MAX_PX;
      const effectiveJpegQuality = jpegQuality ?? LLM_IMAGE_JPEG_QUALITY;
      logger.warn(
        {
          context: logTag,
          effectiveMaxPx,
          format: ocrEnhance ? 'PNG' : 'JPEG',
          jpegQuality: ocrEnhance ? undefined : effectiveJpegQuality,
          ocrEnhance,
        },
        'Payload exceeds safe limit, falling back to resize',
      );
      logger.info(
        { context: 'encode:start', pass: 2, images: images.length, resize: true, maxPx: effectiveMaxPx, mem: fmtMem() },
        'Starting encode pass',
      );
      urls = await encodeImagesInWorker(
        images.map(f => buildEncodeEntry(f, ocrEnhance, true, effectiveMaxPx, effectiveJpegQuality)),
      );
      totalBytes = calcTotalBase64Bytes(urls);
      logger.info(
        {
          context: 'encode:done',
          pass: 2,
          images: urls.length,
          payloadMB: (totalBytes / 1024 / 1024).toFixed(2),
          mem: fmtMem(),
        },
        'Encode pass complete',
      );
      logger.info(
        { context: logTag, payloadMB: (totalBytes / 1024 / 1024).toFixed(2), images: urls.length },
        'After resize payload ready',
      );
    }
  }

  return urls;
}

export async function extractImageFromPDF(pdfID: string): Promise<string[]> {
  // include `createdAt` because AdornisFile/AdornisEntity expects it when wrapping
  // also include common meta fields used later (mimetype, size)
  const projection = { _id: 1, createdAt: 1, meta: { extension: 1, fileName: 1, mimetype: 1, size: 1 } };

  logger.info({ pdfID, context: 'PDF-Extraktion' }, 'Starte PDF-Image-Extraktion');

  // Try to get a PDFFile entity first; if not present, get generic File and wrap.
  let pdfFile;
  try {
    pdfFile = await getByID(PDFFile, pdfID)(projection);
  } catch (err) {
    logger.error({ err, pdfID, context: 'PDF-Extraktion' }, 'Fehler beim Laden der PDF-Datei');
    throw err;
  }

  if (!pdfFile) {
    logger.error({ pdfID, context: 'PDF-Extraktion' }, 'PDF-Datei nicht gefunden');
    throw new Error(`Cannot find file with ID ${pdfID}`);
  }

  let numPages = 0;
  try {
    numPages = await pdfFile.getNumPages();
    logger.info({ pdfID, numPages, context: 'PDF-Extraktion' }, 'PDF Seitenanzahl ermittelt');
  } catch (err) {
    logger.error({ err, pdfID, context: 'PDF-Extraktion' }, 'Fehler beim Auslesen der Seitenzahl');
    throw err;
  }

  if (numPages === 0) {
    logger.warn({ pdfID, context: 'PDF-Extraktion' }, 'PDF hat 0 Seiten');
    return [];
  }

  // FOUR, NOT FIFTEEN. Each slot holds a full-page PNG buffer from `getPDFPageBuffer` plus whatever the
  // rasteriser allocates to produce it, and those are `Buffer`s — EXTERNAL memory, which `--max-old-space-size`
  // does not govern and no heap accounting can see. Fifteen pages of a large scan in flight at once is the most
  // likely cause of the one OOM this service has had, and it was never reachable by any job-level limit: the job
  // system admits ONE submission split, and then this loop fans out fifteen wide inside it.
  //
  // Bounded here rather than at admission because this is where the allocation happens — see `createLimiter`.
  // Per CALL rather than module-scoped: two concurrent splits get four each, which is a deliberate choice to keep
  // one exam's upload from starving another's. If that turns out to be too generous, make it a shared limiter.
  const PDF_EXTRACT_CONCURRENCY = 4;
  logger.info(
    {
      context: 'pdf-extract:start',
      pages: numPages,
      concurrency: Math.min(PDF_EXTRACT_CONCURRENCY, numPages),
      pdfID,
      mem: fmtMem(),
    },
    'Starte PDF-Seiten-Extraktion',
  );
  const t0 = Date.now();

  const ids = new Array<string>(numPages);
  let nextPage = 0;
  const slot = async () => {
    while (nextPage < numPages) {
      const index = nextPage++;
      try {
        logger.info(
          { pdfID, page: index + 1, totalPages: numPages, context: 'PDF-Extraktion' },
          'Konvertiere PDF-Seite',
        );
        const imageBuffer = await pdfFile.getPDFPageBuffer(index);
        logger.info(
          { page: index + 1, sizeBytes: imageBuffer.length, context: 'PDF-Extraktion' },
          'PDF-Seite konvertiert',
        );

        const dir = `./files/${pdfID}`;
        await mkdir(dir, { recursive: true });

        const imageFile = await ImageFile.createFromBuffer(imageBuffer, `${dir}/page-${index}.png`);
        await imageFile.save();
        logger.info(
          { page: index + 1, imageFileId: imageFile._id, context: 'PDF-Extraktion' },
          'PDF-Seite gespeichert',
        );
        ids[index] = imageFile._id;
      } catch (err) {
        logger.error(
          { err, pdfID, page: index + 1, context: 'PDF-Extraktion' },
          'Fehler beim Konvertieren der PDF-Seite',
        );
        throw err;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PDF_EXTRACT_CONCURRENCY, numPages) }, slot));

  logger.info(
    { context: 'pdf-extract:done', pages: ids.length, pdfID, elapsedMs: Date.now() - t0, mem: fmtMem() },
    'PDF-Extraktion abgeschlossen',
  );
  logger.info({ pdfID, pages: ids.length, context: 'PDF-Extraktion' }, 'PDF-Seiten erfolgreich extrahiert');
  return ids;
}
